/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        api.ts
 * @description Configuration du client HTTP Axios pour les appels API REST
 *              Gere l'authentification JWT et les erreurs globales
 *
 * @features    - Injection automatique du token JWT dans les headers
 *              - Gestion des erreurs 401 (redirection vers login)
 *              - Base URL configurable via variable d'environnement
 *
 * @env         REACT_APP_API_URL - URL de base de l'API (defaut: /api)
 *
 * @version     2.1
 * @author      Equipe FALCON
 * =============================================================================
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';

/** URL de base de l'API backend Symfony */
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/** URL de base du backend (sans /api) pour les ressources statiques (photos, uploads) */
export const BACKEND_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

/**
 * Instance Axios configuree pour l'API FALCON
 *
 * Cette instance est pre-configuree avec:
 * - L'URL de base de l'API
 * - Les headers par defaut (Content-Type: application/json)
 * - Les intercepteurs pour l'authentification et la gestion d'erreurs
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gerer les erreurs
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expire ou invalide
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Types d'erreur API
 */
export interface ApiError {
  message?: string;
  // La grande majorite des controllers backend renvoient {"error": "..."} et non
  // {"message": "..."} - les deux sont donc lus ici pour ne pas retomber sur le
  // fallback generique a chaque erreur metier.
  error?: string;
  errors?: string | Record<string, string[]>;
  code?: string;
}

/**
 * Extrait le message d'erreur d'une reponse API
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    const errorsField = typeof data?.errors === 'string' ? data.errors : undefined;
    return data?.error || data?.message || errorsField || error.message || 'Une erreur est survenue';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Une erreur inconnue est survenue';
}

/**
 * Construit l'URL complete d'une ressource statique du backend (photo, upload)
 * Prefixe le chemin relatif avec l'URL du backend si necessaire
 */
export function getBackendUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return BACKEND_BASE_URL + path;
}

export default apiClient;
